import { action, observable } from 'mobx'
import { message } from 'antd'
import _ from 'lodash'

import { StoreExt } from '@utils/reactExt'
import {
  IBetting,
  IBettingStorage,
  IBettingTotalCount,
  ICloseBetting,
  IDeleteBetting,
  Schema$BettorBetFold,
  Schema$BettorBetInfo,
  Schema$BettorBetRecord,
} from '@protocol/my'
import { Schema$Outcome } from '@protocol/game'
import { replaceMarketName } from '@game/util'
import { authStore, globalStore, matchupStore, userPickStore } from '@store/index'
import { BETTING_HISTORY_TAB_KEY } from '@constants/index'
import { getErrorText } from '@game/errtext'
import { showToast, TOAST_TYPE } from '@utils/utils'
import * as ErrorCodes from 'adv-protocol/src/ErrorCodes'
import { BET_TYPE_ON, MATCHUP_CATEGORY, SEARCH_BET_LIST_STATE } from 'adv-protocol/src/GameCommon'
import { PAGE_TYPE } from '@interface/common'
import { ProducerType } from '@game-type/betradar-type'
import { FROM_VIEW_TYPE } from '@v2/constants/common'

export type BettingHistoryParams = IBetting.Params
export type BettingHistorySchema = IBetting.Schema
export type BettingFold = Schema$BettorBetFold
export type BettingInfo = Schema$BettorBetInfo
export type BettingRecord = Schema$BettorBetRecord
export type Outcome = Schema$Outcome

export interface BettingHistoryInitProps {
  onlyMajorClub: boolean
  fromViewType: FROM_VIEW_TYPE
}

export interface BettingFoldData {
  foldId: string // club_bet_fold.id
  marketId: string
  marketType: string
  marketName: string
  specifiers: string
  specialBetValue: string
  selection: string
  outcomeMetaId: string // lsports 의 메타아웃컴 아이디. selection 은 실제 아웃컴의 아이디. lsports 는 아웃컴 아이디가 메타 아이디가 아니다.
  outcomeRate: string
  outcomeResult: string
  hitState: number
  typeOn: BET_TYPE_ON
  matchupCategory: MATCHUP_CATEGORY

  selectionName: string
  outcomeName: string
  oddsFields: Outcome[]
  userPickWritten: boolean
  producerId?: ProducerType
}

export interface BettingFoldGrouped {
  matchId: number
  dateOfMatch: Date
  sportId: number
  sportName: string
  categoryId: number
  categoryName: string
  tournamentId: number
  tournamentName: string
  homeTeamId: number
  homeTeamName: string
  homeTeamEmblem: string
  awayTeamId: number
  awayTeamName: string
  awayTeamEmblem: string

  homeScore?: number
  awayScore?: number
  match_status?: number   // unified_match 에 status
  closeRequest?: boolean  // 마감요청 유무
  closeCompleted?: boolean // 마감요청 완료 유무
  endedDateMatch?: Date    // 종료된 경기 시간

  betCanceled?: boolean
  isOverTimeMatch?: boolean
  data: BettingFoldData[]
  producerId: ProducerType
}

export type BettingFoldGroupedMap = { [id: string]: BettingFoldGrouped }

export interface BettingRecordTrack {
  betId: string // bettor bet id
  firstDateOfMatch: Date
  bet: BettingInfo
  folds: BettingFoldGroupedMap // id:tournamentId
  close1: number
  close2: number
  isSpecial?: boolean
  userPickWritten: boolean
  userPickWrittenAll: boolean
}

export interface BettingRecordSource {
  currentTabKey?: BETTING_HISTORY_TAB_KEY
  tracks?: BettingRecordTrack[]
  lastFetched?: BettingRecordTrack[]
  hasMore?: boolean
  limit?: number
  total?: number
}

export class BettingHistoryStore extends StoreExt {
  fetchTabKey: BETTING_HISTORY_TAB_KEY

  // @observable
  sourceOngoing: BettingRecordSource

  // @observable
  sourceClosed: BettingRecordSource // success or fail

  // @observable
  sourceShared: BettingRecordSource

  // @observable
  sourceStorage: BettingRecordSource

  // @observable
  sourceSpecial: BettingRecordSource

  // 선택된 삭제 리스트
  old_deleteBettorBetIds = []

  @observable
  old_deleteBettorBetIdsCount = 0

  // 선택된 삭제 리스트
  deleteBettorBetIds: { [key: string]: string[] } = {}

  deleteBettorBetIdsCount: { [key: string]: number } = {}

  @observable
  publishPickRecord: BettingRecordTrack

  updator: () => void

  @observable
  selectedBettingHistory: { [key: string]: BettingRecordTrack } = {}

  @observable
  selectedBettingHistoryFold: { [key: string]: BettingFoldGrouped } = {}

  @observable
  selectedBettingHistoryFoldData: { [key: string]: BettingFoldData } = {}

  @observable
  isDeleteAllConfirmOpenByView: { [key: string]: boolean } = {}

  @observable
  onGoingBetCount = 0

  @observable
  onGoingBetCountByOnlyMajorClub = 0

  bettingHistoryInitProps: BettingHistoryInitProps

  @action
  cleanup = () => {
    this.fetchTabKey = undefined
    this.sourceOngoing = undefined
    this.sourceClosed = undefined
    this.sourceStorage = undefined
    this.sourceSpecial = undefined
    this.publishPickRecord = undefined
    this.updator = undefined
    this.selectedBettingHistory = {}
    this.selectedBettingHistoryFold = {}
    this.selectedBettingHistoryFoldData = {}
    this.isDeleteAllConfirmOpenByView = {}
    this.clearBettorBetIds()
  }

  clearBettorBetIds(): void {
    _.chain(this.deleteBettorBetIds).keys().forEach(key => {
      const fromViewType = _.toNumber(key)

      if (_.isNaN(key)) {
        return
      }

      this.deleteBettorBetIds[key] = []
      this.updateDeleteBetterBetIdCount(fromViewType)
    })
  }

  clearBettorBetIdsByFromViewType(fromViewType: FROM_VIEW_TYPE): void {
    this.deleteBettorBetIds[fromViewType] = []
    this.updateDeleteBetterBetIdCount(fromViewType)
  }

  addDeleteBetterBetIds(bettorBetId: string, fromViewType: FROM_VIEW_TYPE): void {
    this.deleteBettorBetIds[fromViewType].push(bettorBetId)
    this.updateDeleteBetterBetIdCount(fromViewType)
  }

  removeDeleteBetterBetIds(targetBettorBetId: string, fromViewType: FROM_VIEW_TYPE): void {
    this.deleteBettorBetIds[`${fromViewType}`] = this.deleteBettorBetIds[`${fromViewType}`].filter(
      bettorBetId => bettorBetId !== targetBettorBetId
    )
    this.updateDeleteBetterBetIdCount(fromViewType)
  }

  @action
  updateDeleteBetterBetIdCount = (fromViewType: FROM_VIEW_TYPE) => {
    this.deleteBettorBetIdsCount[`${fromViewType}`] = this.deleteBettorBetIds[fromViewType].length
  }

  @action
  setIsDeleteAllConfirmOpenByView = (isOpen: boolean, fromViewType: FROM_VIEW_TYPE) => {
    this.isDeleteAllConfirmOpenByView[fromViewType] = isOpen
  }

  @action
  setPublishPickBetting = (betId: string) => {
    let track
    if (betId && this.sourceOngoing) {
      track = _.find(this.sourceOngoing.tracks, { betId })
      this.publishPickRecord = track
    } else {
      this.publishPickRecord = undefined
    }
    return this.publishPickRecord
  }

  @action
  setOnGoingBettingCount = (count: number) => {
    this.onGoingBetCount = count
  }

  @action
  setOnGoingBetCountByOnlyMajorClub = (count: number) => {
    this.onGoingBetCountByOnlyMajorClub = count
  }

  @action
  setSelectedBettingHistoryByBettingRecordTrack = (track: BettingRecordTrack, fromViewType: FROM_VIEW_TYPE): void => {
    this.selectedBettingHistory[fromViewType] = track
  }

  @action
  setSelectedBettingHistoryFold = (fold: BettingFoldGrouped, fromViewType: FROM_VIEW_TYPE): void => {
    this.selectedBettingHistoryFold[fromViewType] = fold
  }

  @action
  setSelectedBettingHistoryFoldData = (foldData: BettingFoldData, fromViewType: FROM_VIEW_TYPE): void => {
    this.selectedBettingHistoryFoldData[fromViewType] = foldData
  }

  @action
  resetSelectedBettingHistory = (fromViewType: FROM_VIEW_TYPE): void => {
    this.selectedBettingHistory[fromViewType] = undefined
    this.selectedBettingHistoryFold[fromViewType] = undefined
    this.selectedBettingHistoryFoldData[fromViewType] = undefined
  }

  /**
   * 분석글 작성이 된 매치가 포함된 베팅은 모두 취소 할 수 없다.
   * 어드민과 PC 클라에서의 취소는 가능하게 한다.
   * 혹시나 어뷰징을 위해 분석글 등록해놓고 취소 못하게 할 수 있으니.
   *
   * @param betId 베터벳 아이디
   */
  @action
  cancelBet = async (betId: string): Promise<boolean> => {
    const res = await this.api.game.doCancelBet({ betId })
    let result = false

    if (res.canceled) {
      result = true
      message.info('베팅 취소가 완료되었습니다.')
    } else {
      const errorText = getErrorText(res.errcode) || '서버 연결이 원활하지 않습니다.'
      switch (res.errcode) {
        case ErrorCodes.ERR.BET_CANCEL_FAIL_TIMEOUT:
        case ErrorCodes.ERR.BET_CANCEL_FAIL_ALREADY_START:
          globalStore.setVisibleBetCancelFailPopup(true)
          break
        case ErrorCodes.ERR.FAIL: {
          message.error(res.errMsg)
          result = true
          break
        }
        default: {
          if (_.isEmpty(_.trim(errorText))) {
            globalStore.setVisibleBetCancelFailPopup(true)
          } else {
            message.error(errorText)
          }
        }
      }
    }

    if (res.userInfo) {
      authStore.updateUserAssets(res.userInfo)
    }

    return result
  }

  @action
  fetchItems = async (
    currentTabKey: BETTING_HISTORY_TAB_KEY,
    limit: number,
    clubId: string,
    pageType?: PAGE_TYPE,
    resetForce = false,
    onlyMajorClub = false,
  ): Promise<BettingRecordSource> => {
    try {
      // state> -1:all, 0:ongoing, 1:success, 2:fail, 3:cancel
      let targetSource = this.sourceOngoing
      switch (currentTabKey) {
        case BETTING_HISTORY_TAB_KEY.ONGOING:
          targetSource = this.sourceOngoing
          break
        case BETTING_HISTORY_TAB_KEY.CLOSED:
          targetSource = this.sourceClosed
          break
        case BETTING_HISTORY_TAB_KEY.SAVED:
          targetSource = this.sourceStorage
          break
        case BETTING_HISTORY_TAB_KEY.SHARED:
          targetSource = this.sourceShared
          break
        case BETTING_HISTORY_TAB_KEY.SPECIAL:
          targetSource = this.sourceSpecial
          return this.sourceSpecial
      }
      const reset = resetForce === true || this.fetchTabKey !== currentTabKey
      const targetLength = targetSource ? targetSource.tracks.length : 0
      // if (reset === true && targetLength > 0) {
      //   // 탭이 바뀌었지만 이전에 로드해논 아이템이 있으면 재활용
      //   return targetSource
      // }
      const offset = reset ? 0 : targetLength

      let state: SEARCH_BET_LIST_STATE

      switch (currentTabKey) {
        case BETTING_HISTORY_TAB_KEY.ONGOING:
          state = 0
          break
        case BETTING_HISTORY_TAB_KEY.CLOSED:
          state = 1
          break
        case BETTING_HISTORY_TAB_KEY.SAVED:
          state = 2
          break
        default:
          state = 0
      }

      let result
      if (currentTabKey === BETTING_HISTORY_TAB_KEY.SHARED) {
        result = await this.api.my.getSharedBettingHistories({ state, offset, limit })
      } else {
        let matchId
        if (pageType === PAGE_TYPE.USER_PICK_DETAIL) {
          matchId = userPickStore.selectedUserPickMatch?.matchId
        } else if (pageType === PAGE_TYPE.MATCHUP_DETAIL_PICK) {
          matchId = matchupStore.detailTrack?.track?.matchId
        }
        result = await this.api.my.getMyBettings({
          state, offset, limit, targetClubId: clubId, matchId, onlyMajorClub,
        })
      }

      this.fetchTabKey = currentTabKey
      if (currentTabKey === BETTING_HISTORY_TAB_KEY.ONGOING) {
        const count = result.total
        if (onlyMajorClub) {
          this.setOnGoingBetCountByOnlyMajorClub(count)
        } else {
          this.setOnGoingBettingCount(count)
        }
      }

      if (reset) {
        return this.resetTracks(currentTabKey, { ...result })
      }
      return this.updateTracks(currentTabKey, { ...result })
    } catch (err) {
      console.error(err)
      message.error('베팅내역 페이지를 정상적으로 불러오지 못하였습니다.')
    }

    return null
  }

  @action
  async getBettingRecordTrack(betId: string): Promise<BettingRecordTrack> {
    const bettorBetRecord = await this.api.bettor.getBettorBetRecord({ betId })
    return this.schema$BettorBetRecordToBettingRecordTrack(bettorBetRecord)
  }

  @action
  resetTracks = (currentTabKey: BETTING_HISTORY_TAB_KEY, fetchData: BettingHistorySchema): BettingRecordSource => {
    switch (currentTabKey) {
      case BETTING_HISTORY_TAB_KEY.ONGOING:
        this.sourceOngoing = {}
        break
      case BETTING_HISTORY_TAB_KEY.CLOSED:
        this.sourceClosed = {}
        break
      case BETTING_HISTORY_TAB_KEY.SHARED:
        this.sourceShared = {}
        break
      case BETTING_HISTORY_TAB_KEY.SAVED:
        this.sourceStorage = {}
        break
      case BETTING_HISTORY_TAB_KEY.SPECIAL:
        this.sourceSpecial = {}
        break
    }

    return this.updateTracks(currentTabKey, fetchData)
  }

  @action
  updateTracks = (currentTabKey: BETTING_HISTORY_TAB_KEY, fetchData: BettingHistorySchema): BettingRecordSource => {
    let targetSource = this.sourceOngoing
    switch (currentTabKey) {
      case BETTING_HISTORY_TAB_KEY.ONGOING:
        targetSource = this.sourceOngoing
        break
      case BETTING_HISTORY_TAB_KEY.CLOSED:
        targetSource = this.sourceClosed
        break
      case BETTING_HISTORY_TAB_KEY.SHARED:
        targetSource = this.sourceShared
        break
      case BETTING_HISTORY_TAB_KEY.SAVED:
        targetSource = this.sourceStorage
        break
      case BETTING_HISTORY_TAB_KEY.SPECIAL:
        targetSource = this.sourceSpecial
        return null
    }
    const { limit, total, items } = fetchData
    const { tracks = [] } = targetSource
    const newTracks: BettingRecordTrack[] = _.cloneDeep(tracks)
    const lastFetched: BettingRecordTrack[] = []

    for (const record of items) {
      // const bet = cloneDeep(el.bet)
      const track = this.schema$BettorBetRecordToBettingRecordTrack(record)
      newTracks.push(track)
      lastFetched.push(track)
    }

    const hasMore = newTracks.length < total
    const newSource = { currentTabKey, tracks: newTracks, lastFetched, hasMore, limit, total }

    switch (currentTabKey) {
      case BETTING_HISTORY_TAB_KEY.ONGOING:
        this.sourceOngoing = newSource
        break
      case BETTING_HISTORY_TAB_KEY.CLOSED:
        this.sourceClosed = newSource
        break
      case BETTING_HISTORY_TAB_KEY.SHARED:
        this.sourceShared = newSource
        break
      case BETTING_HISTORY_TAB_KEY.SAVED:
        this.sourceStorage = newSource
        break
    }

    return newSource
  }

  schema$BettorBetRecordToBettingRecordTrack = (record: Schema$BettorBetRecord): BettingRecordTrack => {
    const folds: BettingFoldGroupedMap = {}
    let firstDateOfMatch = null
    const grouped = _.groupBy(record.folds, 'matchId')
    for (const matchId in grouped) {
      for (const fold of grouped[matchId]) {
        if (!folds[matchId]) {
          folds[matchId] = {
            matchId: fold.matchId,
            dateOfMatch: new Date(fold.dateOfMatch),
            sportId: fold.sportId,
            sportName: fold.sportName,
            categoryId: fold.categoryId,
            categoryName: fold.categoryName,
            tournamentId: fold.tournamentId,
            tournamentName: fold.tournamentName,
            homeTeamId: fold.homeTeamId,
            homeTeamName: fold.homeTeamName,
            homeTeamEmblem: fold.homeTeamEmblem,
            awayTeamId: fold.awayTeamId,
            awayTeamName: fold.awayTeamName,
            awayTeamEmblem: fold.awayTeamEmblem,
            data: [],
            awayScore: fold.awayScore,
            homeScore: fold.homeScore,
            match_status: fold.match_status,
            closeRequest: fold.closeRequest,
            closeCompleted: fold.closeCompleted,
            endedDateMatch: fold.endedDateMatch,
            isOverTimeMatch: fold.isOverTimeMatch,
            producerId: fold.producerId,
          }
        }
        const foldData: BettingFoldData = {
          foldId: fold.foldId,
          marketId: fold.marketId,
          marketType: fold.marketType,
          marketName: fold.marketName,
          specifiers: fold.specifiers,
          specialBetValue: fold.specialBetValue,
          selection: fold.selection,
          outcomeMetaId: fold.outcomeMetaId,
          outcomeRate: fold.outcomeRate,
          outcomeResult: fold.outcomeResult,
          hitState: fold.hitState,
          typeOn: record.bet.typeOn,
          matchupCategory: record.bet.category,

          selectionName: fold.selectionName,
          outcomeName: fold.outcomeName,
          oddsFields: fold.oddsFields,
          userPickWritten: fold.userPickWritten,
          producerId: fold.producerId,
        }

        if (firstDateOfMatch) {
          if (folds[matchId].dateOfMatch.valueOf() < firstDateOfMatch.valueOf()) {
            firstDateOfMatch = new Date(folds[matchId].dateOfMatch)
          }
        } else {
          firstDateOfMatch = new Date(fold.dateOfMatch)
        }

        foldData.marketName = replaceMarketName(
          foldData.marketName,
          folds[matchId].homeTeamName,
          folds[matchId].awayTeamName,
        )

        folds[matchId].data.push(foldData)
      }
    }

    const entries = Object.entries(folds)
    const sortedEntries = _.orderBy(
      entries,
      [
        entry => entry[1].producerId,
        entry => entry[0],
      ],
      ['desc', 'asc'])
    const sortedFolds = sortedEntries.map(([key, value]) => ({ key, ...value })) as unknown as BettingFoldGroupedMap
    return {
      betId: record.betId,
      firstDateOfMatch,
      close1: record.close1,
      close2: record.close2,
      bet: record.bet,
      folds: sortedFolds,
      userPickWritten: record.userPickWritten,
      userPickWrittenAll: record.userPickWrittenAll,
    }
  }

  @action
  fetchSpecialItems = async (amount: number, resetForce = false, binGameType: number): Promise<BettingRecordSource> => {
    try {
      // state> -1:all, 0:ongoing, 1:success, 2:fail, 3:cancel
      const targetSource = this.sourceSpecial

      const reset = resetForce === true
      const targetLength = targetSource ? targetSource.tracks.length : 0
      // if (reset === true && targetLength > 0) {
      //   // 탭이 바뀌었지만 이전에 로드해논 아이템이 있으면 재활용
      //   return targetSource
      // }
      const offset = reset ? 0 : targetLength
      const { limit, total, items } = await this.api.my.getMySpecialBettings({ offset, limit: amount, binGameType })

      if (reset) {
        this.sourceSpecial = {}
      }
      return this.updateSpecialTracks({ limit, total, items })
    } catch (err) {
      console.error(err)

      message.error('배팅내역 구성중 오류가 발생했습니다!')
    }

    return null
  }

  @action
  updateSpecialTracks = (fetchData: BettingHistorySchema): BettingRecordSource => {
    const targetSource = this.sourceSpecial

    const { limit, total, items } = fetchData
    const { tracks = [] } = targetSource
    const newTracks: BettingRecordTrack[] = _.cloneDeep(tracks)
    const lastFetched: BettingRecordTrack[] = []

    for (const el of items) {
      // const bet = cloneDeep(el.bet)
      const folds: BettingFoldGroupedMap = {}
      let firstDateOfMatch = new Date()
      const grouped = _.groupBy(el.folds, 'matchId')
      for (const matchId in grouped) {
        for (const fold of grouped[matchId]) {
          if (!folds[matchId]) {
            folds[matchId] = {
              matchId: fold.matchId,
              dateOfMatch: new Date(fold.dateOfMatch),
              sportId: fold.sportId,
              sportName: fold.sportName,
              categoryId: fold.categoryId,
              categoryName: fold.categoryName,
              tournamentId: fold.tournamentId,
              tournamentName: fold.tournamentName,
              homeTeamId: fold.homeTeamId,
              homeTeamName: fold.homeTeamName,
              homeTeamEmblem: fold.homeTeamEmblem,
              awayTeamId: fold.awayTeamId,
              awayTeamName: fold.awayTeamName,
              awayTeamEmblem: fold.awayTeamEmblem,
              data: [],
              producerId: fold.producerId,
            }
          }
          const foldData: BettingFoldData = {
            foldId: fold.foldId,
            marketId: fold.marketId,
            marketType: fold.marketType,
            marketName: fold.marketName,
            specifiers: fold.specialBetValue,
            specialBetValue: fold.specialBetValue,
            selection: fold.selection,
            outcomeMetaId: fold.outcomeMetaId,
            outcomeRate: fold.outcomeRate,
            outcomeResult: fold.outcomeResult,
            hitState: fold.hitState,
            typeOn: el.bet.typeOn,
            matchupCategory: el.bet.category,

            selectionName: fold.selectionName,
            outcomeName: fold.outcomeName,
            oddsFields: fold.oddsFields,
            userPickWritten: fold.userPickWritten,
          }

          if (folds[matchId].dateOfMatch.valueOf() > firstDateOfMatch.valueOf()) {
            firstDateOfMatch = new Date(fold.dateOfMatch)
          }

          if (fold.specialBetValue) {
            try {
              const tmp = fold.specialBetValue.includes('{') ? JSON.parse(fold.specialBetValue) : fold.specialBetValue
              const val = Object.values(tmp)[0] as string
              foldData.specialBetValue = val || ''
            } catch (err) {
              // leave empty...
            }
          }

          folds[matchId].data.push(foldData)
        }
      }

      const track = {
        betId: el.betId,
        firstDateOfMatch,
        bet: el.bet,
        folds,
        close1: el.close1,
        close2: el.close2,
        isSpecial: true,
        userPickWritten: el.userPickWritten,
        userPickWrittenAll: el.userPickWrittenAll,
      }
      newTracks.push(track)
      lastFetched.push(track)
    }

    const hasMore = newTracks.length < total
    const newSource = { tracks: newTracks, lastFetched, hasMore, limit, total }

    this.sourceSpecial = newSource

    return newSource
  }

  setUpdator = (updator: () => void) => {
    this.updator = updator
  }

  pickPublishedOngoing = (betId: string, pickId: string) => {
    //
    const found = _.find(this.sourceOngoing.tracks, { betId })
    if (found) {
      found.bet.pickId = pickId
      if (this.updator) {
        this.updator()
      }
    }
  }

  saveBettingHistory = async (messageId: string, betId: string, senderId: string) => {
    return this.api.my.saveBettingHistory({ messageId, betId, senderId })
  }

  fetchStorageBettingHistory = async (isStorage: boolean, bettorBetId: string) => {
    try {
      const params: IBettingStorage.Params = {
        isStorage,
        bettorBetId,
      }

      const result = await this.api.my.storageBettingHistory(params)
      if (result.result) {
        const toastMsg = isStorage === true ? '베팅 내역이 보관함으로 이동 되었습니다.' : '베팅 내역이 마감 탭으로 이동 되었습니다.'
        showToast(TOAST_TYPE.INFO, toastMsg)
      }

    } catch (err) {
      console.error(err)
    }
  }

  fetchDeleteBet = async (fromViewType: FROM_VIEW_TYPE): Promise<boolean> => {
    try {
      if (this.deleteBettorBetIds[`${fromViewType}`].length === 0) {
        return false
      }

      const params: IDeleteBetting.Params = {
        bettorBetIds: this.deleteBettorBetIds[`${fromViewType}`],
      }

      const result = await this.api.my.deleteBettingHistory(params)
      if (result.result) {
        showToast(TOAST_TYPE.INFO, '삭제가 완료되었습니다.')
      }

      return result.result
    } catch (err) {
      console.error(err)
    }

    return false
  }

  fetchDeleteAllBet = async (params: { clubId: string }): Promise<boolean> => {
    try {
      const { clubId } = params
      const result = await this.api.my.deleteAllBettingHistory({clubId})
      if (result.result) {
        showToast(TOAST_TYPE.INFO, '삭제가 완료되었습니다.')
      }
      return result.result
    } catch (err) {
      console.error(err)
    }
    return false
  }

  fetchCloseBet = async (
    matchId: number, marketType: string, specifiers: string, bettorBetId: string, selection: string, clubId: string,
  ): Promise<ICloseBetting.Schema> => {
    try {
      const params: ICloseBetting.Params = {
        matchId,
        marketType,
        specifiers,
        bettorBetId,
        selection,
        clubId,
      }

      const result = await this.api.my.closeBettingHistory(params)
      if (result.result) {
        showToast(TOAST_TYPE.INFO, '마감요청이 완료되었습니다.')
      } else if (result.errcode && result.errcode === ErrorCodes.ERR.CLOSED_BET_FOLD) {
        showToast(TOAST_TYPE.INFO, '이미 마감되었습니다.')
      } else {
        showToast(TOAST_TYPE.INFO, '마감요청에 실패하였습니다.')
      }

      return result
    } catch (err) {
      console.error(err)
    }

    return null
  }

  fetchBettingTotalCount = async (closed: boolean, deleted: boolean): Promise<void> => {
    try {
      const params: IBettingTotalCount.Params = {
        closed,
        deleted,
      }

      const { totalCount } = await this.api.my.getMyBettingTotalCount(params)
      this.setOnGoingBettingCount(totalCount)
    } catch (err) {
      console.error(err)
    }
  }

  setBettingHistoryInitProps = (props: BettingHistoryInitProps): void => {
    this.bettingHistoryInitProps = props
  }
}

export default new BettingHistoryStore()
